import { DocumentViewer } from "@repo/ui/components/documents/document-viewer";
import { EmptyState } from "@repo/ui/components/empty-state";
import { createFileRoute } from "@tanstack/react-router";
import { Files } from "lucide-react";
import { DocumentVideo } from "@/features/documents/components/document-video";
import {
  downloadPatientDocument,
  fetchPatientDocumentBytes,
  type PatientDocumentDto,
  usePatientDocuments,
} from "@/shared/edge/queries";
import { patientDomainIdFromProfile } from "@/shared/lib/patient-profile";

export const Route = createFileRoute("/_consented/documents/$documentId")({
  component: Page,
  staticData: { fullBleed: true, crumb: DocumentCrumb },
});

function useDocument(): { document: PatientDocumentDto | undefined; isReady: boolean } {
  const { documentId } = Route.useParams();
  const { profile } = Route.useRouteContext();
  const { documents, isReady } = usePatientDocuments({ patientId: patientDomainIdFromProfile(profile) });
  return { document: documents.find((candidate) => candidate.id === documentId), isReady };
}

function DocumentCrumb(): string | null {
  return useDocument().document?.title ?? null;
}

function Page() {
  const { document, isReady } = useDocument();
  return (
    <div className="h-full min-h-0">
      {!document ? (
        isReady ? (
          <div className="flex h-full">
            <EmptyState className="m-auto p-8">
              <EmptyState.Icon>
                <Files className="size-4" />
              </EmptyState.Icon>
              <EmptyState.Content>
                <EmptyState.Title>Document not found.</EmptyState.Title>
              </EmptyState.Content>
            </EmptyState>
          </div>
        ) : (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        )
      ) : !document.binaryId ? (
        <div className="p-6 text-sm text-muted-foreground">Still uploading…</div>
      ) : document.contentType.startsWith("video/") ? (
        <DocumentVideo id={document.id} title={document.title} />
      ) : (
        <DocumentViewer
          key={document.id}
          documentKey={document.id}
          fetchBytes={() => fetchPatientDocumentBytes(document.id)}
          onDownload={() => downloadPatientDocument(document)}
          contentType={document.contentType}
          title={document.title}
        />
      )}
    </div>
  );
}
